// Mirrors the active order to localStorage so it survives a cookie drop
// (in-app browsers, cookie expiry). We store the order id AND the site it lives
// at, together — the site lets a recovery re-hydrate the order at the correct
// store, and keeps the client copy in lockstep with the server's orderSiteId
// (see setSessionOrder in sessions.ts). The two must never be stored apart.

const KEY = "oo_order"; // JSON: { id, siteId }
const LEGACY_KEY = "oo_order_id"; // pre-orderSiteId: a plain id string

export interface StoredOrder {
  id: string;
  siteId?: string;
}

export const saveOrderId = (id: string, siteId?: string) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ id, siteId }));
    // Drop any legacy plain-id entry so it can't later mask this one.
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // localStorage unavailable (private mode / SSR / blocked) — ignore.
  }
};

/**
 * Read the stored order (id + site), transparently migrating a legacy
 * plain-string entry (which has no site). Returns null when nothing valid is
 * stored or storage is unreadable.
 */
export const readStoredOrder = (): StoredOrder | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredOrder> | null;
      if (parsed && typeof parsed.id === "string" && parsed.id) {
        return {
          id: parsed.id,
          siteId:
            typeof parsed.siteId === "string" ? parsed.siteId : undefined,
        };
      }
    }
    // Legacy fallback: a bare id with no site.
    const legacy = localStorage.getItem(LEGACY_KEY);
    return legacy ? { id: legacy } : null;
  } catch {
    // Unreadable or corrupt — treat as nothing stored.
    return null;
  }
};

/** Back-compat convenience: just the stored order id (no site). */
export const readOrderId = (): string | null => readStoredOrder()?.id ?? null;

export const clearOrderId = () => {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // localStorage unavailable (private mode / SSR / blocked) — ignore.
  }
};
